import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/** Outcome of the cross-origin auth-bridge handshake. */
export interface BridgeResult {
  /** True when the browser has a Brafton session authorized for this round. */
  loggedIn: boolean;
  /** The elevated Brafton session token, when logged in (else null). */
  token: string | null;
}

/**
 * Widget side of the auth bridge. Embeds the main Feedback app's `/auth-bridge`
 * page in a hidden iframe and asks — using the round's validate JWT — whether the
 * current browser is a logged-in Brafton user authorized for this round.
 *
 * Contract (mirrors the bridge page + tools/auth-bridge-test/embed-test.html):
 *  - we postMessage `{ type:'feedback-auth-bridge:request', jwt, feedbackRoundId }`
 *  - the bridge replies `{ source:'feedback-auth-bridge', auth:'success'|'failed', token }`
 *
 * Note: a fully frictionless first-time cross-site grant needs a user gesture in
 * a VISIBLE bridge iframe; this passive (hidden) probe resolves the already-
 * granted / clearly-logged-out cases and otherwise falls back to logged-out.
 */
@Injectable({ providedIn: 'root' })
export class AuthBridgeService {
  private static readonly REQUEST_TYPE = 'feedback-auth-bridge:request';
  private static readonly RESPONSE_SOURCE = 'feedback-auth-bridge';
  private static readonly BRIDGE_PATH = '/auth-bridge';
  private static readonly REPLY_TIMEOUT_MS = 8000;

  detectBraftonSession(appOrigin: string, jwt: string, feedbackRoundId: string): Observable<BridgeResult> {
    const bridgeOrigin = appOrigin.replace(/\/+$/, '');
    const result = new Subject<BridgeResult>();

    let settled = false;
    let iframe: HTMLIFrameElement | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const finish = (loggedIn: boolean, token: string | null): void => {
      if (settled) {
        return;
      }
      settled = true;
      window.removeEventListener('message', onMessage);
      if (timer !== null) {
        clearTimeout(timer);
      }
      iframe?.remove();
      result.next({ loggedIn, token });
      result.complete();
    };

    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== bridgeOrigin) {
        return;
      }
      const data = event.data as { source?: string; auth?: string; token?: string } | null;
      if (!data || data.source !== AuthBridgeService.RESPONSE_SOURCE) {
        return;
      }
      const loggedIn = data.auth === 'success';
      finish(loggedIn, loggedIn ? (data.token ?? null) : null);
    };

    window.addEventListener('message', onMessage);

    iframe = document.createElement('iframe');
    iframe.setAttribute('allow', 'storage-access');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:absolute;left:-9999px;top:0;width:1px;height:1px;border:0;';
    iframe.src = `${bridgeOrigin}${AuthBridgeService.BRIDGE_PATH}`;
    iframe.addEventListener('load', () => {
      iframe?.contentWindow?.postMessage(
        { type: AuthBridgeService.REQUEST_TYPE, jwt, feedbackRoundId },
        bridgeOrigin,
      );
    });
    document.body.appendChild(iframe);

    // If the bridge never replies (app down, blocked, first-time gesture needed),
    // assume logged out so the Sign-in button is shown.
    timer = setTimeout(() => finish(false, null), AuthBridgeService.REPLY_TIMEOUT_MS);

    return result.asObservable();
  }
}
